import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  Link,
  Collapse,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  SwapHoriz as SwapHorizIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { paymentsAPI, paymentAttachmentsAPI, checksAPI, banksAPI } from '../services/api';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Espèces' },
  { value: 'check', label: 'Chèque' },
  { value: 'transfer', label: 'Virement' },
  { value: 'other', label: 'Autre' },
];

const ACCEPTED_FILE_TYPES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif';

let pendingUploadPaymentId = null;

function PaymentModal({ open, onClose, invoice, onPaymentAdded }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const [expandedPayment, setExpandedPayment] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [attachmentsLoading, setAttachmentsLoading] = useState({});
  const [uploadingPayment, setUploadingPayment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const addFormInputRef = useRef(null);

  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    reference: '',
    notes: '',
  });
  const [newPaymentFiles, setNewPaymentFiles] = useState([]);
  const [checkData, setCheckData] = useState({
    check_number: '',
    bank_id: '',
    bank_name: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
  });
  const [banks, setBanks] = useState([]);
  const [sortField, setSortField] = useState('payment_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertPaymentId, setConvertPaymentId] = useState(null);
  const [convertPayment, setConvertPayment] = useState(null);
  const [selectedProofOption, setSelectedProofOption] = useState('new');
  const [newProofFile, setNewProofFile] = useState(null);
  const [convertData, setConvertData] = useState({
    check_number: '',
    bank_name: '',
    due_date: '',
  });
  const [convertSubmitting, setConvertSubmitting] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      loadPayments();
      loadBanks();
      resetForm();
    }
  }, [open, invoice]);

  const resetForm = () => {
    setNewPayment({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: '',
      reference: '',
      notes: '',
    });
    setNewPaymentFiles([]);
    setCheckData({
      check_number: '',
      bank_id: '',
      bank_name: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
    });
    setShowAddForm(false);
    setError(null);
    setExpandedPayment(null);
  };

  const loadBanks = async () => {
    try {
      const response = await banksAPI.getAll();
      const bankList = response.data?.results || response.data || [];
      setBanks(bankList);
    } catch (err) {
      console.error('Error loading banks:', err);
    }
  };

  const loadPayments = async () => {
    if (!invoice) return;
    try {
      setLoading(true);
      const response = await paymentsAPI.getByInvoice(invoice.id);
      setPayments(Array.isArray(response.data) ? response.data : (response.data.results || []));
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async (paymentId) => {
    try {
      setAttachmentsLoading(prev => ({ ...prev, [paymentId]: true }));
      const response = await paymentAttachmentsAPI.getByPayment(paymentId);
      setAttachments(prev => ({ ...prev, [paymentId]: response.data.results || response.data || [] }));
    } catch (err) {
      console.error('Error loading attachments:', err);
    } finally {
      setAttachmentsLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleToggleExpand = (paymentId) => {
    if (expandedPayment === paymentId) {
      setExpandedPayment(null);
    } else {
      setExpandedPayment(paymentId);
      if (!attachments[paymentId]) {
        loadAttachments(paymentId);
      }
    }
  };

  const handleUploadAttachments = async (paymentId, files) => {
    if (!files || files.length === 0) return;
    setUploadingPayment(paymentId);
    const errors = [];
    let uploaded = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const formData = new FormData();
        formData.append('payment', paymentId);
        formData.append('file', files[i]);
        await paymentAttachmentsAPI.create(formData);
        uploaded++;
      } catch (err) {
        errors.push(files[i].name);
      }
    }
    if (errors.length > 0) {
      setError(`Erreur lors de l'upload de: ${errors.join(', ')}`);
    }
    if (uploaded > 0) {
      setSnackbar({ open: true, message: `${uploaded} fichier(s) ajouté(s) avec succès`, severity: 'success' });
    }
    setUploadingPayment(null);
    loadAttachments(paymentId);
  };

  const triggerUpload = (paymentId) => {
    if (uploadingPayment) return;
    pendingUploadPaymentId = paymentId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleUploadInputChange = (e) => {
    const targetId = pendingUploadPaymentId;
    if (!targetId) return;
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    pendingUploadPaymentId = null;
    if (files.length > 0) {
      handleUploadAttachments(targetId, files);
    }
  };

  const triggerAddFormUpload = () => {
    if (addFormInputRef.current) {
      addFormInputRef.current.value = '';
      addFormInputRef.current.click();
    }
  };

  const handleAddFormInputChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setNewPaymentFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (paymentId, attachmentId) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try {
      await paymentAttachmentsAPI.delete(attachmentId);
      setSnackbar({ open: true, message: 'Pièce jointe supprimée', severity: 'success' });
      loadAttachments(paymentId);
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Erreur lors de la suppression');
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    // Validation chèque
    if (newPayment.payment_method === 'check') {
      if (!checkData.check_number.trim()) {
        setError('Veuillez entrer le numéro du chèque');
        return;
      }
    }

    try {
      setSubmitting(true);

      let checkId = null;

      // 1. Créer le chèque d'abord si méthode = chèque
      if (newPayment.payment_method === 'check') {
        const selectedBank = banks.find(b => b.id === checkData.bank_id);
        const checkForm = new FormData();
        checkForm.append('check_number', checkData.check_number.trim());
        if (checkData.bank_id) checkForm.append('bank', checkData.bank_id);
        checkForm.append('bank_name', selectedBank?.name || checkData.bank_name || '');
        checkForm.append('issue_date', checkData.issue_date);
        if (checkData.due_date) checkForm.append('due_date', checkData.due_date);
        checkForm.append('beneficiary', checkData.beneficiary || '');
        checkForm.append('amount', parseFloat(newPayment.amount));
        checkForm.append('currency', 'DZD');
        checkForm.append('direction', 'incoming');
        checkForm.append('check_type', 'cheque');
        checkForm.append('client', invoice?.document?.client?.id || invoice?.client?.id || '');
        const proofFile = newPaymentFiles.length > 0 ? newPaymentFiles[0] : null;
        if (proofFile) {
          checkForm.append('proof_image', proofFile);
          checkForm.append('status', 'pending');
        } else {
          checkForm.append('status', 'draft');
        }

        const checkResp = await checksAPI.create(checkForm);
        checkId = checkResp.data?.id;
      }

      // 2. Créer le paiement
      const paymentRef = newPayment.payment_method === 'check' && !newPayment.reference
        ? checkData.check_number
        : (newPayment.reference || '');
      const paymentForm = new FormData();
      paymentForm.append('invoice', invoice.id);
      paymentForm.append('amount', parseFloat(newPayment.amount));
      paymentForm.append('payment_date', newPayment.payment_date);
      paymentForm.append('payment_method', newPayment.payment_method);
      paymentForm.append('reference', paymentRef);
      paymentForm.append('notes', newPayment.notes || '');
      newPaymentFiles.forEach(file => paymentForm.append('files', file));

      const response = await paymentsAPI.create(paymentForm);

      // 3. Lier le chèque à la facture
      if (checkId && response.data?.id) {
        try {
          await checksAPI.linkInvoice(checkId, {
            invoice_id: invoice.id,
            amount: parseFloat(newPayment.amount),
          });
        } catch (linkErr) {
          console.error('Error linking check to invoice:', linkErr);
        }
      }

      resetForm();
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: 'Paiement ajouté avec succès', severity: 'success' });
    } catch (err) {
      console.error('Error adding payment:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Erreur lors de l'ajout du paiement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPayment = async (paymentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [paymentId]: true }));
      await paymentsAPI.confirm(paymentId);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: 'Paiement confirmé', severity: 'success' });
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError(err.response?.data?.error || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [paymentId]: true }));
      await paymentsAPI.cancel(paymentId);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: 'Paiement annulé', severity: 'info' });
    } catch (err) {
      console.error('Error cancelling payment:', err);
      setError(err.response?.data?.error || "Erreur lors de l'annulation");
    } finally {
      setActionLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return;
    try {
      setActionLoading(prev => ({ ...prev, [paymentId]: true }));
      await paymentsAPI.delete(paymentId);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: 'Paiement supprimé', severity: 'success' });
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleQuickConvertCheck = async (payment) => {
    if (payment.has_check_record) {
      setSnackbar({ open: true, message: 'Ce paiement est déjà enregistré comme chèque', severity: 'info' });
      return;
    }
    try {
      setActionLoading(prev => ({ ...prev, [payment.id]: true }));
      await checksAPI.convertFromPayment({
        payment_id: payment.id,
        check_number: payment.reference || '',
        bank_name: '',
        due_date: '',
        allow_no_proof: true,
      });
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: 'Paiement converti en chèque avec succès', severity: 'success' });
    } catch (err) {
      console.error('Error converting payment to check:', err);
      setError(err.response?.data?.detail || err.response?.data?.payment_id || 'Erreur lors de la conversion');
    } finally {
      setActionLoading(prev => ({ ...prev, [payment.id]: false }));
    }
  };

  const handleChangePaymentMethod = async (paymentId, newMethod) => {
    const payment = payments.find(p => p.id === paymentId);
    if (!payment) return;
    const methodLabel = PAYMENT_METHODS.find(m => m.value === newMethod)?.label || newMethod;
    if (!window.confirm(`Changer la méthode de paiement en "${methodLabel}" ?`)) return;
    try {
      setActionLoading(prev => ({ ...prev, [paymentId]: true }));
      await paymentsAPI.update(paymentId, {
        invoice: payment.invoice,
        amount: payment.amount,
        payment_date: payment.payment_date,
        payment_method: newMethod,
        reference: payment.reference || '',
        notes: payment.notes || '',
      });
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: `Méthode de paiement changée en ${methodLabel}`, severity: 'success' });
    } catch (err) {
      console.error('Error changing payment method:', err);
      setError(err.response?.data?.error || 'Erreur lors du changement de méthode');
    } finally {
      setActionLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleOpenConvert = (payment) => {
    setConvertPaymentId(payment.id);
    setConvertPayment(payment);
    setConvertData({
      check_number: payment.reference || '',
      bank_name: '',
      due_date: '',
    });
    
    const paymentAttachments = attachments[payment.id] || payment.attachments || [];
    if (payment.proof_document) {
      setSelectedProofOption('main');
    } else if (paymentAttachments.length > 0) {
      setSelectedProofOption(paymentAttachments[0].id);
    } else {
      setSelectedProofOption('new');
    }
    setNewProofFile(null);
    setConvertDialogOpen(true);

    if (!attachments[payment.id]) {
      loadAttachments(payment.id);
    }
  };

  const handleCloseConvert = () => {
    setConvertDialogOpen(false);
    setConvertPaymentId(null);
    setConvertPayment(null);
    setConvertData({ check_number: '', bank_name: '', due_date: '' });
    setSelectedProofOption('new');
    setNewProofFile(null);
  };

  const handleSubmitConvert = async () => {
    if (!convertData.check_number.trim()) {
      setError('Veuillez entrer le numéro du chèque');
      return;
    }
    
    if (selectedProofOption === 'new' && !newProofFile) {
      setError('Veuillez ajouter ou choisir un fichier/image de preuve.');
      return;
    }

    try {
      setConvertSubmitting(true);
      
      const formData = new FormData();
      formData.append('payment_id', convertPaymentId);
      formData.append('check_number', convertData.check_number);
      formData.append('bank_name', convertData.bank_name || '');
      if (convertData.due_date) {
        formData.append('due_date', convertData.due_date);
      }
      
      if (selectedProofOption === 'main') {
        formData.append('use_proof_document', 'true');
      } else if (selectedProofOption === 'new') {
        if (newProofFile) {
          formData.append('proof_image', newProofFile);
        }
      } else {
        formData.append('attachment_id', selectedProofOption);
      }

      await checksAPI.convertFromPayment(formData);
      
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
      setSnackbar({ open: true, message: 'Paiement converti en chèque avec succès', severity: 'success' });
      handleCloseConvert();
    } catch (err) {
      console.error('Error converting payment to check:', err);
      setError(err.response?.data?.detail || err.response?.data?.proof_image || err.response?.data?.payment_id || 'Erreur lors de la conversion');
    } finally {
      setConvertSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DA';
  };

  const confirmedTotal = payments
    .filter(p => p.status === 'confirmed')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const pendingTotal = payments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const invoiceTotal = invoice?.total_ttc ? parseFloat(invoice.total_ttc) : 0;
  const remaining = invoiceTotal - confirmedTotal;

  const getAttachmentCount = (paymentId) => {
    return attachments[paymentId]?.length || 0;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedPayments = [...payments].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'amount') {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    } else if (sortField === 'payment_date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Paiements - {invoice?.document?.document_number || 'Facture'}
          </Typography>
          <Chip
            label={formatCurrency(invoiceTotal)}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Total Facture</Typography>
              <Typography variant="h6">{formatCurrency(invoiceTotal)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Payé (confirmé)</Typography>
              <Typography variant="h6" color="success.main">{formatCurrency(confirmedTotal)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">En attente</Typography>
              <Typography variant="h6" color="warning.main">{formatCurrency(pendingTotal)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Reste à payer</Typography>
              <Typography variant="h6" color={remaining > 0 ? 'error.main' : 'success.main'}>
                {formatCurrency(remaining)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => handleSort('payment_date')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Date
                      {sortField === 'payment_date' && (
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('amount')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Montant
                      {sortField === 'amount' && (
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('payment_method')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Méthode
                      {sortField === 'payment_method' && (
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('reference')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Référence
                      {sortField === 'reference' && (
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('status')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Statut
                      {sortField === 'status' && (
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Pièces jointes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Aucun paiement enregistré
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedPayments.map((payment) => {
                    const attachCount = getAttachmentCount(payment.id);
                    return (
                      <React.Fragment key={payment.id}>
                        <TableRow>
                          <TableCell>{payment.payment_date}</TableCell>
                          <TableCell>
                            <strong>{formatCurrency(payment.amount)}</strong>
                          </TableCell>
                          <TableCell>
                            {payment.payment_method_display}
                            {payment.payment_method === 'check' && payment.has_check_record && (
                              <Chip
                                label="Enregistré"
                                color="success"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{payment.reference || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status_display}
                              size="small"
                              color={getStatusColor(payment.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Voir les pièces jointes">
                                <Badge
                                  badgeContent={attachCount}
                                  color="primary"
                                  invisible={attachCount === 0}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleExpand(payment.id)}
                                  >
                                    {expandedPayment === payment.id ? <ExpandLessIcon /> : <AttachFileIcon />}
                                  </IconButton>
                                </Badge>
                              </Tooltip>
                              <Tooltip title="Ajouter des fichiers">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => triggerUpload(payment.id)}
                                    disabled={uploadingPayment !== null}
                                  >
                                    {uploadingPayment === payment.id ? <CircularProgress size={16} /> : <UploadIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {actionLoading[payment.id] ? (
                              <CircularProgress size={20} />
                            ) : payment.status === 'cancelled' ? (
                              <Typography variant="caption" color="text.secondary">
                                Annulé
                              </Typography>
                            ) : (
                              <>
                                {payment.status === 'pending' && (
                                  <>
                                    <Tooltip title="Confirmer">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleConfirmPayment(payment.id)}
                                      >
                                        <CheckIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Annuler">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleCancelPayment(payment.id)}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Supprimer">
                                      <IconButton
                                        size="small"
                                        color="default"
                                        onClick={() => handleDeletePayment(payment.id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                {payment.status === 'confirmed' && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                    Confirmé
                                  </Typography>
                                )}
                                {payment.payment_method !== 'check' && (
                                  <Tooltip title="Convertir en chèque">
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() => handleOpenConvert(payment)}
                                    >
                                      <PaymentIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {payment.payment_method === 'check' && !payment.has_check_record && (
                                  <Tooltip title="Convertir en chèque">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => handleQuickConvertCheck(payment)}
                                    >
                                      <SyncIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Changer la méthode">
                                  <FormControl size="small" sx={{ minWidth: 80, ml: 0.5 }}>
                                    <Select
                                      value={payment.payment_method}
                                      displayEmpty
                                      renderValue={() => <SwapHorizIcon fontSize="small" />}
                                      onChange={(e) => handleChangePaymentMethod(payment.id, e.target.value)}
                                      variant="standard"
                                      disableUnderline
                                      sx={{ '& .MuiSelect-select': { py: 0.5, pl: 1, pr: 0 } }}
                                    >
                                      {PAYMENT_METHODS.map((m) => (
                                        <MenuItem
                                          key={m.value}
                                          value={m.value}
                                          disabled={m.value === payment.payment_method}
                                        >
                                          {m.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={expandedPayment === payment.id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                    Pièces jointes ({attachCount})
                                    {uploadingPayment === payment.id && (
                                      <CircularProgress size={12} sx={{ ml: 1 }} />
                                    )}
                                  </Typography>
                                </Box>
                                {attachmentsLoading[payment.id] ? (
                                  <CircularProgress size={20} />
                                ) : !attachments[payment.id] || attachments[payment.id].length === 0 ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 1 }}>
                                    Aucune pièce jointe
                                  </Typography>
                                ) : (
                                  <List dense disablePadding>
                                    {(attachments[payment.id] || []).map((att) => (
                                      <ListItem key={att.id} sx={{ px: 0 }}>
                                        <ListItemAvatar sx={{ minWidth: 40 }}>
                                          {att.is_image ? (
                                            <Avatar
                                              src={att.file_url}
                                              variant="rounded"
                                              sx={{ width: 40, height: 40 }}
                                            />
                                          ) : (
                                            <Avatar
                                              sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}
                                              variant="rounded"
                                            >
                                              <DescriptionIcon />
                                            </Avatar>
                                          )}
                                        </ListItemAvatar>
                                        <ListItemText
                                          primary={
                                            <Link href={att.file_url} target="_blank" rel="noopener" underline="hover">
                                              {att.name || `Fichier ${att.id}`}
                                            </Link>
                                          }
                                          secondary={att.uploaded_by_name ? `par ${att.uploaded_by_name}` : ''}
                                          primaryTypographyProps={{ variant: 'body2' }}
                                          secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                        <ListItemSecondaryAction>
                                          <Tooltip title="Télécharger">
                                            <IconButton
                                              size="small"
                                              component="a"
                                              href={att.file_url}
                                              target="_blank"
                                              rel="noopener"
                                            >
                                              <DownloadIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Supprimer">
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleDeleteAttachment(payment.id, att.id)}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </ListItemSecondaryAction>
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showAddForm ? (
          <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
            {!newPayment.payment_method ? (
              <>
                <Typography variant="subtitle2" gutterBottom align="center">
                  Choisir la méthode de paiement
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                  {PAYMENT_METHODS.map((m) => (
                    <Grid item xs={6} sm={4} key={m.value}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={() => setNewPayment({ ...newPayment, payment_method: m.value })}
                        sx={{ py: 2, justifyContent: 'center' }}
                      >
                        {m.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button variant="text" onClick={resetForm}>
                    Annuler
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    Nouveau paiement — {PAYMENT_METHODS.find(m => m.value === newPayment.payment_method)?.label}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setNewPayment({ ...newPayment, payment_method: '', amount: '', reference: '', notes: '' });
                      setCheckData({ check_number: '', bank_id: '', bank_name: '', issue_date: new Date().toISOString().split('T')[0], due_date: '' });
                    }}
                  >
                    Changer la méthode
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Montant (DA)"
                      type="number"
                      value={newPayment.amount}
                      onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                      inputProps={{ min: 0, step: 0.01 }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Date"
                      type="date"
                      value={newPayment.payment_date}
                      onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {newPayment.payment_method !== 'check' && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Référence"
                        value={newPayment.reference}
                        onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={newPayment.payment_method === 'check' ? 8 : 6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Notes"
                      value={newPayment.notes}
                      onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                    />
                  </Grid>
                  {newPayment.payment_method === 'check' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Informations du chèque
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="N° chèque"
                          required
                          value={checkData.check_number}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCheckData({ ...checkData, check_number: val });
                            setNewPayment(prev => ({ ...prev, reference: val }));
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="bank-select-label">Banque</InputLabel>
                          <Select
                            labelId="bank-select-label"
                            value={checkData.bank_id}
                            label="Banque"
                            onChange={(e) => {
                              const bankId = e.target.value;
                              const selectedBank = banks.find(b => b.id === bankId);
                              setCheckData({
                                ...checkData,
                                bank_id: bankId,
                                bank_name: selectedBank?.name || '',
                              });
                            }}
                          >
                            <MenuItem value="">
                              <em>-- Sélectionner --</em>
                            </MenuItem>
                            {banks.map((bank) => (
                              <MenuItem key={bank.id} value={bank.id}>
                                {bank.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Date d'émission"
                          type="date"
                          value={checkData.issue_date}
                          onChange={(e) => setCheckData({ ...checkData, issue_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Date d'échéance"
                          type="date"
                          value={checkData.due_date}
                          onChange={(e) => setCheckData({ ...checkData, due_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AttachFileIcon />}
                          onClick={triggerAddFormUpload}
                        >
                          {newPaymentFiles.length > 0
                            ? `${newPaymentFiles.length} fichier(s) sélectionné(s)`
                            : 'Joindre des fichiers'}
                        </Button>
                        {newPaymentFiles.length > 0 && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={triggerAddFormUpload}
                            >
                              Ajouter +
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setNewPaymentFiles([])}
                            >
                              Effacer tout
                            </Button>
                          </>
                        )}
                      </Box>
                      {newPaymentFiles.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {newPaymentFiles.map((file, index) => (
                            <Chip
                              key={index}
                              label={file.name}
                              size="small"
                              onDelete={() => {
                                setNewPaymentFiles(prev => prev.filter((_, i) => i !== index));
                              }}
                              deleteIcon={<CloseIcon />}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => { resetForm(); }}
                        disabled={submitting}
                      >
                        Annuler
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleAddPayment}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
                      >
                        {submitting ? 'Ajout...' : 'Ajouter'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        ) : remaining > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
            >
              Ajouter un paiement
            </Button>
          </Box>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleUploadInputChange}
          accept={ACCEPTED_FILE_TYPES}
        />
        <input
          ref={addFormInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleAddFormInputChange}
          accept={ACCEPTED_FILE_TYPES}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>

      <Dialog open={convertDialogOpen} onClose={handleCloseConvert} maxWidth="sm" fullWidth>
        <DialogTitle>Convertir en chèque</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="N° chèque"
                value={convertData.check_number}
                onChange={(e) => setConvertData({ ...convertData, check_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Banque"
                value={convertData.bank_name}
                onChange={(e) => setConvertData({ ...convertData, bank_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Date d'échéance"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={convertData.due_date}
                onChange={(e) => setConvertData({ ...convertData, due_date: e.target.value })}
              />
            </Grid>
          </Grid>

          {convertPayment && (() => {
            const paymentAttachments = attachments[convertPayment.id] || convertPayment.attachments || [];
            const hasProofDocument = !!convertPayment.proof_document;
            const hasAnyOldFiles = hasProofDocument || paymentAttachments.length > 0;

            return (
              <Box sx={{ mt: 3 }}>
                {hasAnyOldFiles ? (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Sélectionner le document justificatif / Image du chèque :
                    </Typography>
                    
                    {convertPayment.proof_document && (
                      <Box 
                        onClick={() => setSelectedProofOption('main')}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          p: 1, 
                          mb: 1, 
                          border: '1px solid', 
                          borderColor: selectedProofOption === 'main' ? 'primary.main' : 'grey.300', 
                          borderRadius: 1, 
                          cursor: 'pointer',
                          bgcolor: selectedProofOption === 'main' ? 'primary.50' : 'background.paper',
                          '&:hover': { bgcolor: selectedProofOption === 'main' ? 'primary.50' : 'grey.50' }
                        }}
                      >
                        <input 
                          type="radio" 
                          checked={selectedProofOption === 'main'} 
                          onChange={() => setSelectedProofOption('main')} 
                          style={{ cursor: 'pointer' }}
                        />
                        <Avatar 
                          src={convertPayment.proof_document_url} 
                          variant="rounded" 
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            Document justificatif principal
                          </Typography>
                          <Link href={convertPayment.proof_document_url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} sx={{ fontSize: '0.75rem' }}>
                            Visualiser
                          </Link>
                        </Box>
                      </Box>
                    )}

                    {paymentAttachments.map((att) => (
                      <Box 
                        key={att.id}
                        onClick={() => setSelectedProofOption(att.id)}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          p: 1, 
                          mb: 1, 
                          border: '1px solid', 
                          borderColor: selectedProofOption === att.id ? 'primary.main' : 'grey.300', 
                          borderRadius: 1, 
                          cursor: 'pointer',
                          bgcolor: selectedProofOption === att.id ? 'primary.50' : 'background.paper',
                          '&:hover': { bgcolor: selectedProofOption === att.id ? 'primary.50' : 'grey.50' }
                        }}
                      >
                        <input 
                          type="radio" 
                          checked={selectedProofOption === att.id} 
                          onChange={() => setSelectedProofOption(att.id)} 
                          style={{ cursor: 'pointer' }}
                        />
                        {att.is_image ? (
                          <Avatar 
                            src={att.file_url} 
                            variant="rounded" 
                            sx={{ width: 40, height: 40 }}
                          />
                        ) : (
                          <Avatar 
                            sx={{ width: 40, height: 40, bgcolor: 'primary.light' }} 
                            variant="rounded"
                          >
                            <DescriptionIcon />
                          </Avatar>
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {att.name || `Pièce jointe #${att.id}`}
                          </Typography>
                          <Link href={att.file_url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} sx={{ fontSize: '0.75rem' }}>
                            Visualiser
                          </Link>
                        </Box>
                      </Box>
                    ))}

                    <Box 
                      onClick={() => setSelectedProofOption('new')}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5, 
                        p: 1, 
                        mb: 1, 
                        border: '1px solid', 
                        borderColor: selectedProofOption === 'new' ? 'primary.main' : 'grey.300', 
                        borderRadius: 1, 
                        cursor: 'pointer',
                        bgcolor: selectedProofOption === 'new' ? 'primary.50' : 'background.paper',
                        '&:hover': { bgcolor: selectedProofOption === 'new' ? 'primary.50' : 'grey.50' }
                      }}
                    >
                      <input 
                        type="radio" 
                        checked={selectedProofOption === 'new'} 
                        onChange={() => setSelectedProofOption('new')} 
                        style={{ cursor: 'pointer' }}
                      />
                      <Avatar 
                        sx={{ width: 40, height: 40, bgcolor: 'secondary.light' }} 
                        variant="rounded"
                      >
                        <UploadIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">
                          Téléverser un nouveau document justificatif
                        </Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Ce paiement ne contient aucun document justificatif. Vous <strong>devez</strong> téléverser une preuve pour pouvoir le convertir en chèque.
                  </Alert>
                )}

                {selectedProofOption === 'new' && (
                  <Box sx={{ mt: 1.5, p: 2, border: '1px dashed', borderColor: 'grey.400', borderRadius: 1, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      startIcon={<AttachFileIcon />}
                    >
                      Choisir un fichier
                      <input
                        type="file"
                        hidden
                        accept={ACCEPTED_FILE_TYPES}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setNewProofFile(e.target.files[0]);
                          }
                        }}
                      />
                    </Button>
                    {newProofFile && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: 'success.main' }}>
                        Sélectionné : {newProofFile.name} ({(newProofFile.size / 1024).toFixed(1)} KB)
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvert} disabled={convertSubmitting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitConvert}
            disabled={convertSubmitting}
            startIcon={convertSubmitting ? <CircularProgress size={16} /> : null}
          >
            {convertSubmitting ? 'Conversion...' : 'Convertir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default PaymentModal;
